import React, { useEffect, useState, useCallback } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import {
  CalendarBlank, Clock, MapPin, CheckCircle, XCircle, ArrowLeft,
} from '@phosphor-icons/react';
import { publicBookingApi } from '../lib/bookingApi';

const API_BASE = `${window.location.origin}/api`;
const manageClient = axios.create({ baseURL: API_BASE, withCredentials: true });

function PublicShell({ children }) {
  return (
    <div className="min-h-screen bg-[#f5f6fa]">
      <header className="w-full border-b border-border bg-background">
        <div className="max-w-[1200px] mx-auto flex items-center justify-between px-6 py-3">
          <Link to="/login" className="flex items-center gap-3">
            <div className="h-11 w-12 flex items-center justify-center text-white font-heading font-bold text-base tracking-tight"
                 style={{ background: '#002677' }}>BIL</div>
            <div className="hidden sm:block leading-tight">
              <p className="text-sm font-semibold">Schedule &amp; Remind</p>
              <p className="text-xs text-muted-foreground">Manage your booking</p>
            </div>
          </Link>
        </div>
      </header>
      <main className="max-w-[800px] mx-auto px-4 sm:px-6 py-8 sm:py-12">{children}</main>
    </div>
  );
}

export default function ManageBookingPage() {
  const { token } = useParams();
  const [params, setParams] = useSearchParams();
  const initialAction = params.get('action');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState(initialAction === 'cancel' ? 'cancel' : 'view'); // view | cancel | reschedule | done
  const [doneKind, setDoneKind] = useState(null); // 'cancelled' | 'rescheduled'
  const [submitting, setSubmitting] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  // Reschedule state
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const [selectedDate, setSelectedDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await manageClient.get(`/public/manage/${token}`);
      setData(d);
      if (d.event.status === 'cancelled') {
        setMode('done');
        setDoneKind('cancelled');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Booking not found');
    } finally {
      setLoading(false);
    }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const doCancel = async () => {
    setSubmitting(true);
    try {
      await manageClient.post(`/public/manage/${token}/cancel`, { reason: cancelReason });
      toast.success('Booking cancelled');
      setDoneKind('cancelled');
      setMode('done');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cancel failed');
    } finally {
      setSubmitting(false);
    }
  };

  const loadSlots = useCallback(async (d) => {
    if (!data?.profile?.slug || !data?.event_type?.slug) return;
    setSlotsLoading(true);
    try {
      const pad = (n) => String(n).padStart(2, '0');
      const iso = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
      const { data: r } = await publicBookingApi.slots(data.profile.slug, data.event_type.slug, iso);
      setSlots(r.slots || []);
    } catch (_) { setSlots([]); }
    finally { setSlotsLoading(false); }
  }, [data]);

  useEffect(() => {
    if (selectedDate) { loadSlots(selectedDate); setSelectedSlot(null); }
  }, [selectedDate, loadSlots]);

  const doReschedule = async () => {
    if (!selectedSlot) return;
    setSubmitting(true);
    try {
      await manageClient.post(`/public/manage/${token}/reschedule`, { start_at: selectedSlot });
      toast.success('Booking rescheduled');
      setDoneKind('rescheduled');
      setMode('done');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reschedule failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <PublicShell><div className="text-sm text-muted-foreground">Loading…</div></PublicShell>;
  if (!data) {
    return (
      <PublicShell>
        <Card className="rounded-sm p-8 text-center border-dashed">
          <p>Booking not found. The link may have expired.</p>
        </Card>
      </PublicShell>
    );
  }
  const { event, profile, event_type } = data;
  const fmt = (iso) => new Date(iso).toLocaleString(undefined, {
    weekday: 'long', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });

  if (mode === 'done') {
    return (
      <PublicShell>
        <Card className="rounded-sm p-10 text-center border-dashed">
          {doneKind === 'cancelled' ? (
            <>
              <XCircle size={48} weight="fill" className="mx-auto text-destructive" />
              <h2 className="font-heading text-2xl font-bold mt-4">Booking cancelled</h2>
              <p className="text-sm text-muted-foreground mt-2">
                Your appointment with <strong>{profile.name}</strong> has been cancelled.
                A confirmation has been emailed to both parties.
              </p>
            </>
          ) : (
            <>
              <CheckCircle size={48} weight="fill" className="mx-auto text-[#00B140]" />
              <h2 className="font-heading text-2xl font-bold mt-4">Rescheduled!</h2>
              <p className="text-sm text-muted-foreground mt-2">
                Your appointment with <strong>{profile.name}</strong> is now on <strong>{fmt(event.start_at)}</strong>.
                A new confirmation email is on the way.
              </p>
            </>
          )}
        </Card>
      </PublicShell>
    );
  }

  return (
    <PublicShell>
      <Card className="rounded-sm p-6">
        <div className="flex items-start gap-4">
          <div className="h-12 w-1 rounded-sm bg-[#002677]" style={{ background: event_type?.color || '#002677' }} />
          <div className="flex-1">
            <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">Your appointment with {profile.name}</p>
            <h1 className="font-heading text-2xl font-bold mt-1">{event.title}</h1>
            <div className="mt-3 space-y-1.5 text-sm">
              <div className="inline-flex items-center gap-2"><Clock size={14} className="text-[#002677]" /> {fmt(event.start_at)} · {event.duration_minutes} min</div>
              {event.location && (
                <div className="flex items-start gap-2"><MapPin size={14} className="text-[#002677] mt-0.5" /><span>{event.location}</span></div>
              )}
              <div className="inline-flex items-center gap-2"><CalendarBlank size={14} className="text-[#002677]" /> {event.customer_name} · {event.customer_email}</div>
            </div>
          </div>
        </div>
        {mode === 'view' && (
          <div className="flex flex-col sm:flex-row gap-3 mt-6">
            <Button
              onClick={() => setMode('reschedule')}
              className="rounded-sm bg-[#002677] hover:bg-[#002677]/90 text-white"
              data-testid="manage-reschedule-btn"
            >
              Reschedule
            </Button>
            <Button
              onClick={() => setMode('cancel')}
              variant="outline"
              className="rounded-sm text-destructive border-destructive hover:bg-destructive/10"
              data-testid="manage-cancel-btn"
            >
              Cancel booking
            </Button>
          </div>
        )}
      </Card>

      {mode === 'cancel' && (
        <Card className="rounded-sm p-6 mt-4" data-testid="manage-cancel-panel">
          <h3 className="font-heading font-semibold text-lg">Cancel your booking</h3>
          <p className="text-sm text-muted-foreground mt-1">
            We&apos;ll let {profile.name} know. Want to share a reason? (optional)
          </p>
          <Textarea rows={3} value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} className="rounded-sm mt-3" />
          <div className="flex gap-3 mt-4">
            <Button onClick={doCancel} disabled={submitting} variant="destructive" className="rounded-sm"
                    data-testid="manage-confirm-cancel">
              {submitting ? 'Cancelling…' : 'Yes, cancel'}
            </Button>
            <Button onClick={() => setMode('view')} variant="ghost" className="rounded-sm">
              Keep booking
            </Button>
          </div>
        </Card>
      )}

      {mode === 'reschedule' && (
        <Card className="rounded-sm p-6 mt-4" data-testid="manage-reschedule-panel">
          <button onClick={() => setMode('view')} className="text-sm text-muted-foreground inline-flex items-center gap-1 mb-4">
            <ArrowLeft size={14} /> Back
          </button>
          <h3 className="font-heading font-semibold text-lg">Pick a new time</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
            <div>
              <label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">New date</label>
              <input
                type="date"
                className="block w-full mt-1.5 h-10 rounded-sm border border-input bg-background px-3 text-sm"
                onChange={(e) => setSelectedDate(e.target.value ? new Date(e.target.value + 'T00:00:00') : null)}
                data-testid="manage-resched-date"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">Available times</label>
              {!selectedDate ? (
                <p className="text-sm text-muted-foreground mt-2">Pick a date to see times.</p>
              ) : slotsLoading ? (
                <p className="text-sm text-muted-foreground mt-2">Loading…</p>
              ) : slots.length === 0 ? (
                <p className="text-sm text-muted-foreground mt-2">No availability on this day.</p>
              ) : (
                <div className="grid grid-cols-3 gap-1.5 max-h-[200px] overflow-y-auto mt-2 pr-1">
                  {slots.map((iso) => {
                    const active = selectedSlot === iso;
                    return (
                      <button key={iso} type="button" onClick={() => setSelectedSlot(iso)}
                              data-testid={`manage-slot-${iso}`}
                              className={`py-1.5 rounded-sm text-xs font-medium border transition-colors ${
                                active ? 'bg-[#002677] border-[#002677] text-white'
                                       : 'border-[#002677]/30 text-[#002677] hover:border-[#002677]'
                              }`}>
                        {new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          <Button onClick={doReschedule} disabled={!selectedSlot || submitting}
                  className="rounded-sm bg-[#002677] hover:bg-[#002677]/90 mt-4"
                  data-testid="manage-confirm-reschedule">
            {submitting ? 'Rescheduling…' : 'Confirm new time'}
          </Button>
        </Card>
      )}
    </PublicShell>
  );
}
